import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import type { Match } from '../../../../../../api/useSupplierReconciliation/types/match'
import { ProductsCellRenderer } from '../renderers/ProductsCellRenderer'
import { TerminalsCellRenderer } from '../renderers/TerminalsCellRenderer'
import { DestinationsCellRenderer } from '../renderers/DestinationsCellRenderer'
import { CheckCircleFilled } from '@ant-design/icons'
import { Horizontal } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'

interface ServerParams {
  params?: any[]
}

function getFilterOptionsFromServerParams(serverParams?: ServerParams, fieldName?: string): string[] | undefined {
  if (!serverParams?.params || !fieldName) return undefined

  // Find the matching param by field name or title
  const matchingParam = serverParams.params.find(
    (param: any) =>
      param.filter_column === fieldName ||
      param.title?.toLowerCase() === fieldName.toLowerCase() ||
      param.title?.toLowerCase().includes(fieldName.toLowerCase())
  )

  if (matchingParam?.options && matchingParam.datatype === 'dropdown') {
    return matchingParam.options.map((option: any) => option.value || option.label || option)
  }

  return undefined
}

function formatSourceValue(value: string): string {
  if (value === 'manual_entry') {
    return 'Manual Entry'
  }
  return value
}

export function getMetadataColumns(serverParams?: ServerParams): ColDef<Match>[] {
  return [
    // Products column
    {
      colId: 'products',
      headerName: 'PRODUCTS',
      field: 'products',
      flex: 1.5,
      minWidth: 130,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getFilterOptionsFromServerParams(serverParams, 'products'),
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      cellRenderer: ProductsCellRenderer,
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.products || !Array.isArray(params.data.products)) return ''
        return params.data.products.join(', ')
      },
      filterValueGetter: (params) => {
        if (!params.data?.products || !Array.isArray(params.data.products)) return null
        return params.data.products
      },
    },

    // Terminals column
    {
      colId: 'terminals',
      headerName: 'TERMINALS',
      field: 'terminals',
      flex: 1.5,
      minWidth: 150,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getFilterOptionsFromServerParams(serverParams, 'terminals'),
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      cellRenderer: TerminalsCellRenderer,
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.terminals || !Array.isArray(params.data.terminals)) return ''
        return params.data.terminals.join(', ')
      },
      filterValueGetter: (params) => {
        if (!params.data?.terminals || !Array.isArray(params.data.terminals)) return null
        return params.data.terminals
      },
    },

    // Destinations column
    {
      colId: 'destinations',
      headerName: 'DESTINATIONS',
      field: 'destinations',
      flex: 1.5,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getFilterOptionsFromServerParams(serverParams, 'destinations'),
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      cellRenderer: DestinationsCellRenderer,
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.destinations?.length) return ''
        return params.data.destinations.map((d) => `${d.site_number}, ${d.site_name}`).join(', ')
      },
      filterValueGetter: (params) => {
        if (!params.data?.destinations?.length) return ''
        return params.data.destinations.map((d) => `${d.site_number}, ${d.site_name}`).join(', ')
      },
    },

    // Invoice Source column
    {
      colId: 'source_name',
      headerName: 'SOURCE',
      field: 'source_name',
      flex: 1,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: getFilterOptionsFromServerParams(serverParams, 'source_name'),
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      // This controls both cell display and grouping
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.source_name) return null
        return formatSourceValue(params.data.source_name)
      },
      // This controls what appears in filter dropdown
      filterValueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data?.source_name) return null
        return formatSourceValue(params.data.source_name)
      },
      cellRenderer: (params: ICellRendererParams<Match>) => {
        return params.value // valueGetter already transformed it
      },
    },
    {
      field: 'export_status',
      headerName: 'EXPORT STATUS',
      hide: true,
      cellRenderer: (params: ICellRendererParams<Match>) => {
        if (params.value === 'sent') {
          return (
            <Horizontal width='100%' justifyContent='center'>
              <Tooltip title='Exported'>
                <CheckCircleFilled style={{ color: 'var(--theme-success)', fontSize: 16 }} />
              </Tooltip>
            </Horizontal>
          )
        }
      },
    },
  ]
}
